import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { ChecklistExecutionSnapshotTargetType } from 'constants/checklists';

import { selectApplicationChecklists } from './checklists.selectors';
import { selectCompletenessChecklistIdSet } from './completenessChecklists.selectors';
import { selectLoanApplicationChecklistResultsList } from './loanApplicationChecklistResults.selectors';

/**
 * Derives the processor/UW-facing "Suppressed fields" accountability list for a
 * single checklist (DEV-4387 / FE-6): every field-linked checklist item an LO has
 * suppressed by marking a field "Not needed" (FE-3), with WHO suppressed it + WHEN.
 *
 * A suppression is the existing checklist OVERRIDE (`itemsToOverride`), which is
 * audited server-side. This selector filters the checklist's items to those that
 * are BOTH field-linked (`fieldRef != null`) AND currently overridden, and resolves
 * the override provenance — mirroring how `selectIncompleteLinkedFields` reads the
 * same override signals, but surfacing the actor + date instead of hiding the chip.
 *
 * `isOverridden` matches the field-completeness indicators (DEV-4468): the top-level
 * `wasOverriden` flag (set once the override is committed) OR an `Override` entry in
 * `overrideHistory.entries` (set while the override is still a pending change).
 *
 * Provenance prefers the APPLIED override's `overridenBy`/`overrideDate` and falls
 * back to the latest `overrideHistory.entries[].change` — its `byUserId`/`createdDate`
 * (with impersonation attribution) — exactly as BE-2 delivers it.
 *
 * The item text + `fieldRef` come from the checklist DEFINITION
 * (`checklist.categories[].items[]`); the override provenance comes from the RESULT
 * items (`items[]`, keyed by `checklistItemId`) — the same two halves the alerts
 * table already merges.
 *
 * @param {string} checklistId
 * @returns selector → Array<{
 *   id: string, checklistId: string, itemName: string, fieldRef: string,
 *   overrideActorUserId: (string|null), overrideActorUserName: (string|null),
 *   overrideDate: (string|null)
 * }>
 */
const isItemOverridden = ({ wasOverriden, overrideHistory }) =>
  !!wasOverriden || !!overrideHistory?.entries?.some(entry => entry?.change?.type === 'Override');

const getLatestOverrideEntryChange = overrideHistory => {
  const overrideEntries = (overrideHistory?.entries ?? []).filter(
    entry => entry?.change?.type === 'Override',
  );

  if (!overrideEntries.length) {
    return null;
  }

  return _.maxBy(overrideEntries, entry => entry.change.createdDate ?? '')?.change ?? null;
};

const resolveOverrideProvenance = result => {
  if (!result || !isItemOverridden(result)) {
    return null;
  }

  const { overridenBy, overridenByName, overrideDate } = result;

  // Prefer the applied (non-pending) override actor + date.
  if (overridenBy || overrideDate) {
    return {
      overrideActorUserId: overridenBy ?? null,
      // FH-3004: the flat read resolves this name BE-side onto the item; the legacy (flag-OFF)
      // read carries no such field, so the row falls back to a FE user-id lookup.
      overrideActorUserName: overridenByName ?? null,
      overrideDate: overrideDate ?? null,
    };
  }

  // Fall back to the richest audit trail — the latest history override entry.
  const latestChange = getLatestOverrideEntryChange(result.overrideHistory);

  return {
    overrideActorUserId: latestChange?.byUserId ?? null,
    overrideActorUserName: latestChange?.byUserName ?? null,
    overrideDate: latestChange?.createdDate ?? null,
  };
};

export const selectSuppressedFieldLinks = memoizeOne(checklistId =>
  createSelector(selectApplicationChecklists, checklists => {
    const rawChecklist = checklists?.[checklistId];
    const categories = rawChecklist?.checklist?.categories;

    if (!categories) {
      return [];
    }

    // Borrower-target snapshot → its rows navigate to THAT borrower's drawer;
    // loan-wide snapshots carry no borrower (the caller falls back to primary).
    const borrowerId =
      rawChecklist.target?.targetType === ChecklistExecutionSnapshotTargetType.BORROWER
        ? rawChecklist.target.targetId
        : null;

    const resultsMap = _.keyBy(rawChecklist.items, 'checklistItemId');
    const rows = [];

    categories.forEach(category => {
      category.items?.forEach(item => {
        if (!item.fieldRef) {
          return;
        }

        const provenance = resolveOverrideProvenance(resultsMap[item.id]);

        if (!provenance) {
          return;
        }

        rows.push({
          id: item.id,
          checklistId,
          itemName: item.name,
          fieldRef: item.fieldRef,
          borrowerId,
          ...provenance,
        });
      });
    });

    return rows;
  }),
);

/**
 * Loan-wide per-checklist suppressed counts for the nav badge (DEV-5146) —
 * `{ [checklistId]: count }`. The nav-level sibling of `selectSuppressedFieldLinks`:
 * instead of the checklist DEFINITION (only loaded once its Alerts subtab opens) it
 * reads the always-loaded execution snapshots, exactly like `selectIncompleteLinkedNav`
 * does for "Needed" — so the badge is live from anywhere in the loan. Scope-limited to
 * the company's completeness checklists (DEV-4603), matching the in-editor highlighting.
 *
 * Snapshot items count when field-linked AND overridden — the inverse discrimination
 * of the needed roll-up, via the same `isItemOverridden`. A per-borrower field
 * suppressed for two borrowers counts twice (each is a real suppression of its own
 * snapshot target), mirroring the needed-count semantics.
 */
export const selectSuppressedFieldLinksCountByChecklist = memoizeOne(() =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const countByChecklist = {};

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const items = snapshot.status?.result?.items;

        if (!checklistId || !items || !completenessChecklistIdSet.has(checklistId)) {
          return;
        }

        items.forEach(item => {
          if (!item.fieldRef || !isItemOverridden(item)) {
            return;
          }

          countByChecklist[checklistId] = (countByChecklist[checklistId] ?? 0) + 1;
        });
      });

      return countByChecklist;
    },
  ),
);
